import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, MessageCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { mockData } from '../data/mockData';

export const Contacto = () => {
  const { contactFields } = mockData;
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    nombre: '',
    institucion: '',
    correo: '',
    telefono: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    let result;

    try {
      const response = await fetch('/emailer.php', {
        method: 'POST',
        body: new FormData(e.target),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      console.log(text); // Verifica qué responde PHP
      result = JSON.parse(text);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Ocurrió un error al enviar el mensaje.",
      });
      
      setIsSubmitting(false);
      return;
    }

    toast({
      title: result.success ? "Mensaje enviado" : "Error",
      description: result.message,
    });
    
    if (result.success) {
      setFormData({
        nombre: '',
        institucion: '',
        correo: '',
        telefono: '',
        mensaje: ''
      });
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="Contact and communication"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-burgundy-50/65 to-white/75"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-burgundy-900 mb-6">
              Contacto
            </h1>
            <p className="text-xl text-burgundy-700 mb-8 max-w-3xl mx-auto">
              ¿Tienes preguntas sobre el proceso de acreditación?
              Estamos aquí para ayudarte en tu camino hacia una universidad promotora de la salud
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-burgundy-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-burgundy-700" />
                </div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Correo Electrónico</h3>
                <p className="text-gray-600 mb-4">Escríbenos directamente</p>
                <a href="mailto:dvallin@certification-grana.org" className="text-burgundy-700 hover:text-burgundy-800 font-medium">
                  dvallin@certification-grana.org
                </a> <br />
                <a href="mailto:accreditationgrana@gmail.com" className="text-burgundy-700 hover:text-burgundy-800 font-medium">
                  accreditationgrana@gmail.com
                </a> <br />
                <a href="mailto:donatovallingonzalez@gmail.com" className="text-burgundy-700 hover:text-burgundy-800 font-medium">
                  donatovallingonzalez@gmail.com
                </a>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-burgundy-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-8 w-8 text-burgundy-700" />
                </div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Teléfono</h3>
                <p className="text-gray-600 mb-4">Llámanos durante horario laboral</p>
                <a href="tel:+16506902130" className="text-burgundy-700 hover:text-burgundy-800 font-medium">
                  USA: +1 (650) 690 2130
                </a>
                <br />
                <a href="tel:+523173851203" className="text-burgundy-700 hover:text-burgundy-800 font-medium">
                  México: +52 (317) 385 1203
                </a>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8">
                <div className="w-16 h-16 bg-burgundy-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-8 w-8 text-burgundy-700" />
                </div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Ubicación</h3>
                <p className="text-gray-600 mb-4">Oficina Internacional GRANA</p>
                <p className="text-burgundy-700 font-medium">
                  2350 Alamo Ave. Suite 301
                  <br />Albuquerque, NM, EEUU, 87106
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            {/* Form */}
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-burgundy-800 flex items-center">
                  <MessageCircle className="h-6 w-6 mr-2" />
                  Envíanos un Mensaje
                </CardTitle>
                <p className="text-gray-600">
                  Completa el formulario y nos pondremos en contacto contigo
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {contactFields.map((field) => (
                    <div key={field.name}>
                      <Label htmlFor={field.name} className="text-burgundy-800">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      {field.type === 'textarea' ? (
                        <Textarea
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          required={field.required}
                          rows={4}
                          className="mt-1"
                          placeholder={`Ingresa tu ${field.label.toLowerCase()}`}
                        />
                      ) : (
                        <Input
                          type={field.type}
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleInputChange}
                          required={field.required}
                          className="mt-1"
                          placeholder={`Ingresa tu ${field.label.toLowerCase()}`}
                        />
                      )}
                    </div>
                  ))}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-burgundy-700 hover:bg-burgundy-800 text-white"
                  >
                    {isSubmitting ? (
                      <>Enviando...</>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar Mensaje
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Additional Information */}
            <div className="space-y-8">
              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-burgundy-800">
                    Horario de Atención
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lunes - Viernes:</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sábados:</span>
                      <span className="font-medium">10:00 AM - 2:00 PM</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Domingos:</span>
                      <span className="font-medium">Cerrado</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-xl text-burgundy-800">
                    Preguntas Frecuentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-burgundy-700 mb-1">
                        ¿Cuánto tiempo toma el proceso?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        El proceso completo típicamente toma entre 12-18 meses.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-burgundy-700 mb-1">
                        ¿Qué requisitos previos necesito?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        La institución debe tener compromiso institucional formal.
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-burgundy-700 mb-1">
                        ¿Hay costo por la evaluación?
                      </h4>
                      <p className="text-gray-600 text-sm">
                        Contáctanos para información detallada sobre costos.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-burgundy-900 text-white">
                <CardHeader>
                  <CardTitle className="text-xl">
                    ¿Necesitas Ayuda Inmediata?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-burgundy-100 mb-4">
                    Mándanos un mensaje por Whatsapp 
                  </p>
                  <Button className="bg-white text-burgundy-900 hover:bg-gray-100">
                    <a
                      href={
                        /Mobi|Android/i.test(navigator.userAgent)
                          ? "https://wa.me/+523173851203?text=Requiero informacion sobre el programa"
                          : "https://web.whatsapp.com/send?phone=+523173851203&text=Requiero informacion sobre el programa"
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center"
                    >
                      Abrir Whatsapp
                    </a>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};